import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { frameTitle, isFrameObject } from "../editor/frame/frameContext";
import type { WbBoardScene } from "../lib/board/wb-board-scene";

export type BoardSignalEvent = {
  peerId: string | null;
  displayLabel: string | null;
  kind: string;
  payload: Record<string, unknown>;
  ts: number;
  isSelf: boolean;
};

type PresenceSignal = {
  myPeerId?: string | null;
  sendSignal?: (kind: string, payload?: Record<string, unknown>) => boolean;
} | null;

export type FramePresentState = {
  active: boolean;
  isPresenter: boolean;
  presenterLabel: string | null;
  frameIds: string[];
  index: number;
};

function listFrameIdsInOrder(scene: InstanceType<typeof WbBoardScene> | null): string[] {
  if (!scene) return [];
  return scene
    .getObjects()
    .filter(isFrameObject)
    .map((o) => ({
      id: o.id,
      x: typeof o.x === "number" ? o.x : 0,
      y: typeof o.y === "number" ? o.y : 0,
      title: frameTitle(o),
    }))
    .sort((a, b) => a.y - b.y || a.x - b.x || a.title.localeCompare(b.title, "ru"))
    .map((f) => f.id);
}

export function useFramePresentation(opts: {
  presenceRef: RefObject<PresenceSignal>;
  sceneRef: RefObject<InstanceType<typeof WbBoardScene> | null>;
  canPresent: boolean;
  ready: boolean;
  navigateToFrame: (frameId: string, opts?: { select?: boolean }) => void;
  onSignal?: (ev: BoardSignalEvent) => void;
}) {
  const { presenceRef, sceneRef, canPresent, ready, navigateToFrame, onSignal } = opts;
  const [state, setState] = useState<FramePresentState>({
    active: false,
    isPresenter: false,
    presenterLabel: null,
    frameIds: [],
    index: 0,
  });
  const stateRef = useRef(state);
  stateRef.current = state;

  const applyGoto = useCallback(
    (frameIds: string[], index: number) => {
      const id = frameIds[index];
      if (id) navigateToFrame(id, { select: false });
    },
    [navigateToFrame]
  );

  const handleSignal = useCallback(
    (ev: BoardSignalEvent) => {
      onSignal?.(ev);
      if (ev.kind === "frame_present_start") {
        const frameIds = Array.isArray(ev.payload.frameIds)
          ? (ev.payload.frameIds as unknown[]).filter((x): x is string => typeof x === "string")
          : [];
        const index =
          typeof ev.payload.index === "number" && Number.isFinite(ev.payload.index)
            ? Math.max(0, Math.floor(ev.payload.index))
            : 0;
        if (frameIds.length === 0) return;
        setState({
          active: true,
          isPresenter: Boolean(ev.isSelf),
          presenterLabel: ev.displayLabel,
          frameIds,
          index: Math.min(index, frameIds.length - 1),
        });
        // Снимаем выделение — иначе сверху всплывает меню фрейма поверх «← →»
        sceneRef.current?.setSelection([]);
        if (!ev.isSelf) applyGoto(frameIds, Math.min(index, frameIds.length - 1));
        return;
      }
      if (ev.kind === "frame_present_goto") {
        const cur = stateRef.current;
        if (!cur.active) return;
        const index =
          typeof ev.payload.index === "number" && Number.isFinite(ev.payload.index)
            ? Math.max(0, Math.floor(ev.payload.index))
            : cur.index;
        const frameId =
          typeof ev.payload.frameId === "string" ? ev.payload.frameId : cur.frameIds[index];
        const nextIndex = Math.min(Math.max(0, index), cur.frameIds.length - 1);
        setState((s) => ({ ...s, index: nextIndex }));
        if (frameId) navigateToFrame(frameId, { select: false });
        return;
      }
      if (ev.kind === "frame_present_stop") {
        setState({
          active: false,
          isPresenter: false,
          presenterLabel: null,
          frameIds: [],
          index: 0,
        });
      }
    },
    [applyGoto, navigateToFrame, onSignal, sceneRef]
  );

  const start = useCallback(() => {
    if (!canPresent || !ready) return false;
    const frameIds = listFrameIdsInOrder(sceneRef.current);
    if (frameIds.length === 0) return false;
    const ok = presenceRef.current?.sendSignal?.("frame_present_start", {
      frameIds,
      index: 0,
    });
    if (!ok) return false;
    setState({
      active: true,
      isPresenter: true,
      presenterLabel: null,
      frameIds,
      index: 0,
    });
    navigateToFrame(frameIds[0]!, { select: false });
    return true;
  }, [canPresent, navigateToFrame, presenceRef, ready, sceneRef]);

  const stop = useCallback(() => {
    const cur = stateRef.current;
    if (!cur.active) return;
    if (cur.isPresenter) {
      presenceRef.current?.sendSignal?.("frame_present_stop", {});
    }
    setState({
      active: false,
      isPresenter: false,
      presenterLabel: null,
      frameIds: [],
      index: 0,
    });
  }, [presenceRef]);

  const goTo = useCallback(
    (index: number) => {
      const cur = stateRef.current;
      if (!cur.active || !cur.isPresenter || cur.frameIds.length === 0) return;
      const next = Math.min(Math.max(0, index), cur.frameIds.length - 1);
      const frameId = cur.frameIds[next]!;
      setState((s) => ({ ...s, index: next }));
      navigateToFrame(frameId, { select: false });
      presenceRef.current?.sendSignal?.("frame_present_goto", { index: next, frameId });
    },
    [navigateToFrame, presenceRef]
  );

  const next = useCallback(() => {
    const cur = stateRef.current;
    if (!cur.active) return;
    goTo(cur.index + 1);
  }, [goTo]);

  const prev = useCallback(() => {
    const cur = stateRef.current;
    if (!cur.active) return;
    goTo(cur.index - 1);
  }, [goTo]);

  useEffect(() => {
    if (!state.active || !state.isPresenter) return;
    function onKey(ev: KeyboardEvent) {
      const t = ev.target as HTMLElement | null;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable)) {
        return;
      }
      if (ev.key === "Escape") {
        ev.preventDefault();
        stop();
        return;
      }
      if (ev.key === "ArrowRight" || ev.key === "PageDown") {
        ev.preventDefault();
        next();
        return;
      }
      if (ev.key === "ArrowLeft" || ev.key === "PageUp") {
        ev.preventDefault();
        prev();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, state.active, state.isPresenter, stop]);

  return { state, start, stop, next, prev, goTo, handleSignal };
}
